import React, { useState, useEffect } from 'react'
import MaterialTable from 'material-table'
import {
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@material-ui/core'
import { Block, CheckCircle, Add, Edit } from '@material-ui/icons'
import { getUsuarios, registrarUsuario, actualizarUsuario } from '../../services/RutaSeguraService'

// Normaliza la respuesta de la API al formato de la tabla (id, nombre, cedula, celular, bloqueado)
const mapUsuarioFromApi = (item) => ({
  id: item.id_usuario ?? item.id,
  id_usuario: item.id_usuario ?? item.id,
  nombre: item.nombre_completo ?? item.nombre,
  nombre_completo: item.nombre_completo ?? item.nombre,
  cedula: item.cedula ?? '',
  celular: item.celular ?? '',
  estado: item.estado ?? 1,
  bloqueado: (item.estado ?? 1) === 0,
})

const RutaSeguraUsuariosContainer = () => {
  const [usuarios, setUsuarios] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [addError, setAddError] = useState('')
  const [newUsuario, setNewUsuario] = useState({
    nombre_completo: '',
    cedula: '',
    celular: '',
  })
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [editUsuario, setEditUsuario] = useState(null)
  const [editError, setEditError] = useState('')
  const [guardandoEdit, setGuardandoEdit] = useState(false)

  const getUsuariosData = async () => {
    setIsLoading(true)
    try {
      const response = await getUsuarios()
      const list = response?.success ?? response?.usuarios ?? response?.data ?? (Array.isArray(response) ? response : [])
      setUsuarios(list.map(mapUsuarioFromApi))
    } catch (err) {
      setUsuarios([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getUsuariosData()
  }, [])

  const handleToggleBlock = async (rowData) => {
    const nuevoEstado = rowData.bloqueado ? 1 : 0
    try {
      await actualizarUsuario({
        id_usuario: rowData.id_usuario ?? rowData.id,
        nombre_completo: rowData.nombre_completo ?? rowData.nombre,
        cedula: String(rowData.cedula ?? ''),
        celular: String(rowData.celular ?? ''),
        estado: nuevoEstado,
      })
      const updatedUsuarios = usuarios.map((usuario) =>
        (usuario.id_usuario ?? usuario.id) === (rowData.id_usuario ?? rowData.id)
          ? { ...usuario, bloqueado: !usuario.bloqueado, estado: nuevoEstado }
          : usuario
      )
      setUsuarios(updatedUsuarios)
    } catch (err) {
      // Opcional: mostrar toast de error
    }
  }

  const handleAddUsuario = async () => {
    if (!newUsuario.nombre_completo?.trim() || !newUsuario.celular?.trim()) {
      return
    }
    setAddError('')
    try {
      const celularVal = newUsuario.celular.trim().replace(/\D/g, '')
      await registrarUsuario({
        nombre_completo: newUsuario.nombre_completo.trim(),
        cedula: (newUsuario.cedula ?? '').trim(),
        celular: celularVal ? Number(celularVal) || newUsuario.celular.trim() : newUsuario.celular.trim(),
      })
      setNewUsuario({ nombre_completo: '', cedula: '', celular: '' })
      setOpenAddDialog(false)
      await getUsuariosData()
    } catch (err) {
      setAddError(err?.response?.data?.message || err?.message || 'Error al registrar el usuario')
    }
  }

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false)
    setAddError('')
    setNewUsuario({ nombre_completo: '', cedula: '', celular: '' })
  }

  const handleOpenEditDialog = (rowData) => {
    setEditUsuario({
      id_usuario: rowData.id_usuario ?? rowData.id,
      nombre_completo: rowData.nombre_completo ?? rowData.nombre ?? '',
      cedula: String(rowData.cedula ?? ''),
      celular: String(rowData.celular ?? ''),
      estado: rowData.estado ?? 1,
    })
    setEditError('')
    setOpenEditDialog(true)
  }

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false)
    setEditUsuario(null)
    setEditError('')
  }

  const handleSaveEditUsuario = async () => {
    if (!editUsuario?.id_usuario) return
    const nombre = (editUsuario.nombre_completo ?? '').trim()
    const celular = String(editUsuario.celular ?? '').trim()
    if (!nombre) {
      setEditError('El nombre es requerido')
      return
    }
    setEditError('')
    setGuardandoEdit(true)
    try {
      const cedula = String(editUsuario.cedula ?? '').trim()
      await actualizarUsuario({
        id_usuario: editUsuario.id_usuario,
        nombre_completo: nombre,
        cedula,
        celular,
        estado: editUsuario.estado ?? 1,
      })
      handleCloseEditDialog()
      await getUsuariosData()
    } catch (err) {
      setEditError(err?.response?.data?.message || err?.message || 'Error al actualizar el usuario')
    } finally {
      setGuardandoEdit(false)
    }
  }

  const columns = [
    {
      title: 'ID',
      field: 'id',
      width: 100,
    },
    {
      title: 'Nombre',
      field: 'nombre',
    },
    {
      title: 'Cédula',
      field: 'cedula',
    },
    {
      title: 'Celular',
      field: 'celular',
    },
    {
      title: 'Acciones',
      field: 'acciones',
      render: (rowData) => (
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleOpenEditDialog(rowData)}
            title="Editar nombre y celular"
          >
            <Edit />
          </IconButton>
          <Button
            variant="outlined"
            color="default"
            startIcon={rowData.bloqueado ? <CheckCircle /> : <Block />}
            onClick={() => handleToggleBlock(rowData)}
            size="small"
            style={{ textTransform: 'none' }}
          >
            {rowData.bloqueado ? 'Desbloquear' : 'Bloquear'}
          </Button>
        </Box>
      ),
      sorting: false,
      filtering: false,
    },
  ]

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          Usuarios - Ruta Segura
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setOpenAddDialog(true)}
        >
          Agregar Usuario
        </Button>
      </Box>

      <MaterialTable
        title=""
        columns={columns}
        data={usuarios}
        isLoading={isLoading}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          search: true,
          headerStyle: {
            backgroundColor: '#4c75aa',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
          },
          rowStyle: (rowData) => ({
            fontSize: '14px',
            opacity: rowData.bloqueado ? 0.5 : 1,
            backgroundColor: rowData.bloqueado ? '#f5f5f5' : 'white',
            textDecoration: rowData.bloqueado ? 'line-through' : 'none',
          }),
        }}
        localization={{
          body: {
            emptyDataSourceMessage: 'No hay usuarios disponibles',
          },
          pagination: {
            labelRowsSelect: 'filas',
            labelDisplayedRows: '{from}-{to} de {count}',
          },
          toolbar: {
            searchPlaceholder: 'Buscar',
          },
        }}
      />

      {/* Modal para agregar usuario */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: 12, paddingBottom: 8 },
        }}
      >
        <DialogTitle style={{ paddingBottom: 8, fontSize: '1.25rem', fontWeight: 600 }}>
          Agregar usuario
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 24 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {addError && (
              <Typography color="error" variant="body2" style={{ marginBottom: 4 }}>
                {addError}
              </Typography>
            )}
            <br />
            <TextField
              label="Nombre completo"
              value={newUsuario.nombre_completo}
              onChange={(e) => setNewUsuario({ ...newUsuario, nombre_completo: e.target.value })}
              fullWidth
              variant="outlined"
              required
              autoFocus
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <br />
            <TextField
              label="Cédula"
              value={newUsuario.cedula}
              onChange={(e) => setNewUsuario({ ...newUsuario, cedula: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <br />
            <TextField
              label="Número de celular"
              value={newUsuario.celular}
              onChange={(e) => setNewUsuario({ ...newUsuario, celular: e.target.value })}
              fullWidth
              variant="outlined"
              required
              type="tel"
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                pattern: '[0-9]*',
                maxLength: 15,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', gap: 12 }}>
          <Button
            onClick={handleCloseAddDialog}
            variant="outlined"
            color="default"
            style={{ textTransform: 'none', minWidth: 90 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddUsuario}
            color="primary"
            variant="contained"
            disabled={!newUsuario.nombre_completo?.trim() || !newUsuario.celular?.trim()}
            style={{ textTransform: 'none', minWidth: 90 }}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para editar usuario (nombre y celular) */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: 12, paddingBottom: 8 },
        }}
      >
        <DialogTitle style={{ paddingBottom: 8, fontSize: '1.25rem', fontWeight: 600 }}>
          Editar usuario
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 24 }}>
          {editUsuario && (
            <Box display="flex" flexDirection="column" gap={2.5}>
              {editError && (
                <Typography color="error" variant="body2" style={{ marginBottom: 4 }}>
                  {editError}
                </Typography>
              )}
              <br />
              <TextField
                label="Nombre completo"
                value={editUsuario.nombre_completo}
                onChange={(e) =>
                  setEditUsuario({ ...editUsuario, nombre_completo: e.target.value })
                }
                fullWidth
                variant="outlined"
                required
                autoFocus
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <br />
              <TextField
                label="Cédula"
                value={editUsuario.cedula}
                onChange={(e) =>
                  setEditUsuario({ ...editUsuario, cedula: e.target.value })
                }
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <br />
              <TextField
                label="Número de celular"
                value={editUsuario.celular}
                onChange={(e) =>
                  setEditUsuario({ ...editUsuario, celular: e.target.value })
                }
                fullWidth
                variant="outlined"
                required
                type="tel"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  pattern: '[0-9]*',
                  maxLength: 15,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', gap: 12 }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            color="default"
            style={{ textTransform: 'none', minWidth: 90 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEditUsuario}
            color="primary"
            variant="contained"
            disabled={
              guardandoEdit ||
              !editUsuario?.nombre_completo?.trim() ||
              !editUsuario?.celular?.toString().trim()
            }
            style={{ textTransform: 'none', minWidth: 90 }}
          >
            {guardandoEdit ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RutaSeguraUsuariosContainer
